import { Classes } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { Dialog } from 'vet-bones/components';
import { isEqual } from 'vet-bones/utils';
import { useDidValueChange } from 'vet-bones/utils/hooks';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import {
  MAX_CSV_PARSE_LINES,
  MAX_CSV_UPLOAD_BYTES,
} from 'src/constants/limits';
import { TEST_IDS } from 'src/constants/testIds';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { getResourceKey } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { didOnlyTheHeaderOptionChange } from 'src/containers/ProjectDiagram/utils/resource/didOnlyTheHeaderOptionChange';
import { updateMappingsForResourceFields } from 'src/containers/ProjectDiagram/utils/resource/updateMappingsForResourceFields';
import { updateResourceName } from 'src/containers/ProjectDiagram/utils/resource/updateResourceName';
import { ProjectFileUploadPreviewPanel } from 'src/containers/ProjectFileUploadPreviewDialog/ProjectFileUploadPreviewPanel';
import { useAutomaticallySetHeadersOption } from 'src/containers/ProjectResourceDialog/ProjectFileUploadCreationPanel/hooks';
import { ProjectResourcePreviewDialogFooter } from 'src/containers/ProjectResourceDialog/ProjectResourcePreviewDialogFooter';
import { projectFileUploadSelector } from 'src/selectors/projectFileUpload';
import {
  ProjectFileUpload,
  ProjectFileUploadOptions,
  defaultProjectFileUploadOptions,
} from 'src/store/state/projects';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { doesFileHaveDuplicateHeaders } from 'src/utils/csv/doesFileHaveDuplicateHeaders';
import { unparseCsv } from 'src/utils/csv/unparseCsv';
import { useProjectFileUploadParser } from 'src/utils/hooks/useProjectFileUploadParser';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { readFile } from 'src/utils/readFile';

const useProjectFileUpload = (fileId?: string) => {
  const { projectId } = useRouterParams();
  return useReduxSelector(projectFileUploadSelector(projectId, fileId));
};

export const ProjectFileUploadPreviewDialog: React.VFC = () => {
  const diagram = useDiagram();
  const dispatch = useReduxDispatch();
  const { projectId } = useRouterParams();
  const { isOpen, resourceId } = useReduxSelector(
    (state) => state.ui.fileUploadPreviewDialog
  );
  const fileUpload = useProjectFileUpload(resourceId);

  // this is tracked separately, because replaced files may only
  // have minute differences from the originals (meaning they have
  // same path/names and we can't find differences unless we compare content,
  // which we shouldn't do)
  const [isFileReplaced, setIsFileReplaced] = React.useState(false);
  const [localFile, setLocalFile] = React.useState<ProjectFileUpload | null>(
    null
  );
  const [localFileObject, setLocalFileObject] = React.useState<File | null>(
    null
  );
  const [localName, setLocalName] = React.useState('');
  const [localOptions, setLocalOptions] =
    React.useState<ProjectFileUploadOptions>(defaultProjectFileUploadOptions);

  const { isThereDuplicateHeaders } = useAutomaticallySetHeadersOption({
    files: localFile ? [localFile.contents] : [],
    fileOptions: localOptions,
    setFileOptions: setLocalOptions,
  });

  const { abortParser, isParsing, errors, fields, rows, updateFieldLabel } =
    useProjectFileUploadParser(
      localFileObject || localFile?.contents || null,
      localOptions,
      localFile?.partial ? MAX_CSV_PARSE_LINES : undefined,
      localFile?.fields
    );

  const onDropAccepted = React.useCallback(
    async (files: File[]) => {
      if (!fileUpload) {
        return;
      }

      const [file] = files;
      const isFilePartial = file.size > MAX_CSV_UPLOAD_BYTES;
      const fileContents = await (isFilePartial
        ? Promise.resolve('')
        : readFile(file));

      const doesFileHaveDupeHeaders = await doesFileHaveDuplicateHeaders(
        fileUpload.options
      )(file);
      const newOptions: ProjectFileUploadOptions = {
        ...fileUpload.options,
        header: doesFileHaveDupeHeaders ? false : fileUpload.options.header,
      };
      const newProjectFileUpload: ProjectFileUpload = {
        id: fileUpload.id,
        path: file.name,
        name: fileUpload.name,
        contents: fileContents,
        fields: [], // updated when saved (after parse)
        options: newOptions,
        dateLastUpdated: fileUpload.dateLastUpdated, // updated when saved (in reducer)
        partial: isFilePartial,
      };
      setIsFileReplaced(true);
      setLocalFile(newProjectFileUpload);
      setLocalFileObject(file);
      setLocalOptions(newOptions);

      if (isFilePartial) {
        dispatch(projectsActionCreators.uploadedPartialCsv());
      }
    },
    [fileUpload, dispatch]
  );

  const didFileUploadChange = useDidValueChange(fileUpload);
  React.useEffect(() => {
    if (didFileUploadChange && fileUpload) {
      setLocalFile(fileUpload);
    }
  }, [didFileUploadChange, fileUpload]);

  const errorText = React.useMemo(() => {
    if (!localOptions.separator) {
      return copy.components.projectResourceDialog.projectFileUploadPanel
        .validation.separator;
    }
    if (!localOptions.quote) {
      return copy.components.projectResourceDialog.projectFileUploadPanel
        .validation.quote;
    }
    if (!localOptions.escape) {
      return copy.components.projectResourceDialog.projectFileUploadPanel
        .validation.escape;
    }
    if (errors.length) {
      return copy.components.projectResourceDialog.projectFileUploadPanel
        .validation.errors;
    }
    if (!rows.length) {
      return copy.components.projectResourceDialog.projectFileUploadPanel
        .validation.empty;
    }
    if (!localName) {
      return copy.components.projectResourceDialog.projectFileUploadPanel
        .validation.name;
    }
    return '';
  }, [errors, localName, localOptions, rows]);

  const warningText = React.useMemo(() => {
    if (!fileUpload) {
      return '';
    }

    const areOptionsChanged = !isEqual(fileUpload.options, localOptions);
    if (areOptionsChanged || isFileReplaced) {
      return copy.components.projectResourceDialog.projectFileUploadPanel
        .warningTitle;
    }

    return '';
  }, [fileUpload, isFileReplaced, localOptions]);

  const isDirty = React.useMemo(() => {
    if (!localFile || isFileReplaced) {
      return true;
    }
    const isNameChanged = localName !== localFile.name;
    const areOptionsChanged = !isEqual(localFile.options, localOptions);
    const areFieldsChanged = !isEqual(localFile.fields, fields);

    return isNameChanged || areOptionsChanged || areFieldsChanged;
  }, [localFile, isFileReplaced, localName, localOptions, fields]);

  const handleCloseDialog = React.useCallback(() => {
    abortParser.current();
    setIsFileReplaced(false);
    setLocalFile(null);
    setLocalFileObject(null);
    setLocalName('');
    setLocalOptions(defaultProjectFileUploadOptions);
    dispatch(uiSlice.actions.toggledFileUploadPreviewDialog({ isOpen: false }));
  }, [abortParser, dispatch]);

  const handleSave = React.useCallback(() => {
    if (!localFile || !fileUpload) {
      return;
    }

    const contents =
      localFile.partial && fields.length > 0
        ? unparseCsv({
            fields: fields.map(({ value }) => value),
            rows,
            fileOptions: localOptions,
          })
        : localFile.contents;

    dispatch(
      projectsActionCreators.updatedFileUploadForProject(projectId, {
        name: localName,
        path: localFile.path,
        contents,
        options: localOptions,
        fields,
        id: fileUpload.id,
        partial: localFile.partial,
      })
    );

    if (diagram) {
      commitDiagramModel(diagram, (model) => {
        if (localName !== fileUpload.name) {
          updateResourceName(fileUpload.id, localName)(model);
        }
        const resourceKey = getResourceKey(fileUpload.id);
        const isHeaderOptionChangedOnly = didOnlyTheHeaderOptionChange(
          fileUpload.options,
          localOptions
        );
        updateMappingsForResourceFields({
          areFieldReferencesKept:
            isHeaderOptionChangedOnly &&
            fields.length === fileUpload.fields.length,
          newFields: fields,
          oldFields: fileUpload.fields,
          resourceKey,
        })(model);
      });

      diagram.undoManager.clear();
    }

    handleCloseDialog();
  }, [
    diagram,
    dispatch,
    fields,
    fileUpload,
    handleCloseDialog,
    localFile,
    localName,
    localOptions,
    projectId,
    rows,
  ]);

  const didLocalFileChange = useDidValueChange(localFile);
  React.useEffect(() => {
    if (didLocalFileChange && localFile) {
      setLocalName(localFile.name);
      setLocalOptions(localFile.options);
    }
  }, [didLocalFileChange, localFile]);

  return (
    <Dialog
      autoFocus={false}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      className={classNames('sd-dialog-project-resource', 'sd-dialog-large')}
      closeIconTestId={TEST_IDS.dialog.closeIcon}
      enforceFocus={false}
      isOpen={isOpen}
      lazy={false}
      onClose={handleCloseDialog}
      title={copy.components.fileUploadPreviewDialog.title}
    >
      <div className={Classes.DIALOG_BODY}>
        {localFile ? (
          <ProjectFileUploadPreviewPanel
            errors={errors}
            fields={fields}
            fileName={localName}
            fileOptions={localOptions}
            filePath={localFile.path}
            isFilePartial={localFile.partial}
            isParsing={isParsing}
            isThereDuplicateHeaders={isThereDuplicateHeaders}
            onFileUpload={onDropAccepted}
            rows={rows}
            setFileName={setLocalName}
            setFileOptions={setLocalOptions}
            updateFieldLabel={updateFieldLabel}
            warningText={warningText}
          />
        ) : null}
      </div>
      <ProjectResourcePreviewDialogFooter
        backButtonText={copy.components.projectResourceDialog.cancelButton}
        isSubmitDisabled={!isDirty || Boolean(errorText)}
        onBack={handleCloseDialog}
        onSubmit={handleSave}
        submitButtonText={copy.components.projectResourceDialog.saveButton}
        submitErrorText={errorText}
      />
    </Dialog>
  );
};
